// Persistence middleware for Redux store
const STORAGE_KEY = 'cyberdefense_portfolio_state';

// List of slices to persist
const PERSISTED_SLICES = ['projects', 'skills', 'experience', 'certifications', 'about'];

// Load persisted state from localStorage
export const loadPersistedState = () => {
  try {
    const serializedState = localStorage.getItem(STORAGE_KEY);
    if (serializedState === null) {
      return undefined;
    }
    const parsedState = JSON.parse(serializedState);
    
    // Add timestamp check to handle data migrations
    const currentVersion = '1.0.0';
    if (parsedState.version !== currentVersion) {
      console.log('State version mismatch, using initial state');
      return undefined;
    }
    
    return parsedState.state;
  } catch (err) {
    console.error('Error loading persisted state:', err);
    return undefined;
  }
};

// Save state to localStorage
export const saveState = (state) => {
  try {
    const stateToSave = {};
    
    // Only save specified slices
    PERSISTED_SLICES.forEach(slice => {
      if (state[slice]) {
        stateToSave[slice] = state[slice];
      }
    });
    
    const persistedData = {
      version: '1.0.0',
      timestamp: Date.now(),
      state: stateToSave
    };
    
    const serializedState = JSON.stringify(persistedData);
    localStorage.setItem(STORAGE_KEY, serializedState);
  } catch (err) {
    console.error('Error saving state:', err);
  }
};

// Throttle function to limit save frequency
const throttle = (func, delay) => {
  let timeoutId;
  let lastExecTime = 0;
  
  return function (...args) {
    const currentTime = Date.now();
    
    if (currentTime - lastExecTime > delay) {
      func.apply(this, args);
      lastExecTime = currentTime;
    } else {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
        lastExecTime = Date.now();
      }, delay - (currentTime - lastExecTime));
    }
  };
};

// Create throttled save function (save at most once every 1 second)
const throttledSave = throttle(saveState, 1000);

// Persistence middleware
export const persistenceMiddleware = (store) => (next) => (action) => {
  const result = next(action);
  
  // Only persist if action affects persisted slices
  const actionType = action.type;
  const shouldPersist = PERSISTED_SLICES.some(slice => 
    actionType.startsWith(`${slice}/`) || actionType.includes(`${slice}`)
  );
  
  if (shouldPersist) {
    throttledSave(store.getState());
  }
  
  return result;
};

// Clear persisted state (useful for admin logout or reset)
export const clearPersistedState = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    console.error('Error clearing persisted state:', err);
  }
};

// Export state for backup/restore
export const exportState = () => {
  try {
    const state = localStorage.getItem(STORAGE_KEY);
    if (state) {
      const blob = new Blob([state], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `portfolio-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  } catch (err) {
    console.error('Error exporting state:', err);
  }
};

// Import state from backup
export const importState = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        resolve(data);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}; 